import Wordmark from "../assets/MURMURATIONS.svg";
import Bg from "../assets/splash.webp";
import { isAndroid, openInChrome, openArCoreUpdate } from "../utils/arSupport";

/**
 * Distinct ways an AR launch can fail on the WebXR (Android/desktop) path.
 * Each maps to a plain-English message + a short code shown on screen so a
 * photo of the failure is enough to triage it (see COPY below).
 */
export type ArErrorKind =
  | "permission" // immersive-ar session refused (blocked prompt, or device can't run it)
  | "camera" // camera permission denied
  | "camera-busy" // camera held by another app / tab (NotReadableError)
  | "camera-notfound" // no usable camera on the device (NotFoundError / constraints)
  | "motion" // motion / orientation permission denied
  | "not-supported" // immersive-ar / WebXR not supported on this device or browser
  | "undefined" // immersive-ar session failed/stalled to start (enterXRAsync rejected or timed out)
  | "wrong-browser" // Android, but not a WebXR-capable browser (Firefox / Samsung / Opera…)
  | "in-app-browser" // opened inside an app's in-app webview (Instagram / Facebook / TikTok…)
  | "load" // 3D assets / story queue failed to load (loadAsync rejected)
  | "crash" // WebGL context lost — GPU/memory crash mid-launch or mid-session
  | "timeout"; // launch sat on the loading screen too long with no other signal

/** Recovery actions a failure screen can offer, in addition to "Back to site". */
type ArErrorAction = "retry" | "chrome" | "arcore";

export interface ArError {
  kind: ArErrorKind;
  /**
   * Optional raw technical detail (e.g. an error message). Rendered small in
   * the debug footer for screenshots / triage — never required for the
   * user-facing copy.
   */
  detail?: string;
}

interface ArErrorCopy {
  /** Short, stable code printed on screen for screenshots / bug reports. */
  code: string;
  title: string;
  body: string;
  /**
   * Recovery buttons, in display order, shown above "Back to site". `chrome`
   * and `arcore` only render on Android (they're Android-specific actions).
   * `retry` only renders when an onRetry handler is supplied.
   */
  actions?: ArErrorAction[];
}

const COPY: Record<ArErrorKind, ArErrorCopy> = {
  permission: {
    code: "AR-PERMISSION",
    title: "AR permission needed",
    body: "AR access looks blocked for this site. To turn it on: tap the lock or settings icon next to the web address, open Permissions (or Site settings), enable Augmented Reality and Camera, then reload and try again. If AR still won't start, your device or browser may not support it.",
    actions: ["retry"],
  },
  camera: {
    code: "AR-CAMERA",
    title: "Camera access needed",
    body: "Camera access is blocked, and AR can't run without it. To turn it on: tap the lock or settings icon next to the web address, open Permissions (or Site settings), enable Camera, then reload and try again.",
    actions: ["retry"],
  },
  "camera-busy": {
    code: "AR-CAMERA-BUSY",
    title: "Your camera is in use",
    body: "Another app or tab is using the camera, so AR can't start it. Close anything else that might be using it — a video call, the Camera app, or another browser tab — then try again.",
    actions: ["retry"],
  },
  "camera-notfound": {
    code: "AR-CAMERA-NOTFOUND",
    title: "No camera available",
    body: "We couldn't find a camera to use for AR. Make sure your device has a camera that isn't disabled or blocked, then try again.",
    actions: ["retry"],
  },
  motion: {
    code: "AR-MOTION",
    title: "Motion access needed",
    body: "Motion & orientation access is blocked, and AR needs it. To turn it on: tap the lock or settings icon next to the web address, open Permissions (or Site settings), enable Motion sensors, then reload and try again.",
  },
  "not-supported": {
    code: "AR-UNSUPPORTED",
    title: "AR isn't supported here",
    body: "This device or browser can't run the AR experience. On Android, open the link in Chrome and make sure \"Google Play Services for AR\" is installed and up to date — then try again.",
    actions: ["arcore"],
  },
  undefined: {
    code: "AR-START-FAILED",
    title: "AR couldn't start",
    body: "The AR session didn't start. This is sometimes a temporary hiccup, or \"Google Play Services for AR\" may need an update. Try again — and if it keeps happening, install or update Google Play Services for AR below.",
    actions: ["retry", "arcore"],
  },
  "wrong-browser": {
    code: "AR-WRONG-BROWSER",
    title: "Open in Chrome for AR",
    body: "This AR experience runs in Google Chrome on Android. Open this page in Chrome, then tap Launch again.",
    actions: ["chrome"],
  },
  "in-app-browser": {
    code: "AR-IN-APP-BROWSER",
    title: "Open in Chrome for AR",
    body: "You're viewing this inside another app's built-in browser, which can't run AR. Open it in Chrome — use the button below, or tap the ⋯ menu and choose \"Open in Chrome\".",
    actions: ["chrome"],
  },
  load: {
    code: "AR-CONTENT-LOAD",
    title: "Couldn't load the experience",
    body: "The 3D scene failed to load — this is usually a network problem. Check your connection and try again.",
    actions: ["retry"],
  },
  crash: {
    code: "AR-GPU-LOST",
    title: "AR stopped unexpectedly",
    body: "The AR scene ran out of graphics memory or the display driver reset. Close other apps and browser tabs to free up memory, then try again.",
    actions: ["retry"],
  },
  timeout: {
    code: "AR-TIMEOUT",
    title: "This is taking too long",
    body: "AR didn't start in time. This can happen on a slow connection, if a permission prompt was left open, or if Google Play Services for AR needs an update. Try again below.",
    actions: ["retry", "arcore"],
  },
};

interface ArErrorScreenProps {
  error: ArError;
  /**
   * Return to the microsite (should also reset the half-built AR engine).
   * Optional: the iOS App Clip has no in-frame microsite, so it may omit Back
   * and offer only "Try again" — the Back button then doesn't render.
   */
  onBack?: () => void;
  /** Re-attempt the launch in place (shown as "Try again" for recoverable kinds). */
  onRetry?: () => void;
}

/**
 * Full-screen failure surface for the WebXR launch flow. Replaces the loading
 * spinner when a launch can't complete: tells the user in plain English what
 * went wrong, gives them a way back to the microsite, and prints a stable
 * error code (plus optional technical detail) for screenshots and triage.
 *
 * The iOS App Clip path surfaces its own guidance via Splash.tsx
 * (useXRErrorGuidance); this is the equivalent for the Android/desktop overlay
 * in App.tsx, which previously had no error surface at all.
 */
export function ArErrorScreen({ error, onBack, onRetry }: ArErrorScreenProps) {
  const copy = COPY[error.kind] ?? COPY.timeout;
  const android = isAndroid();

  // Resolve the per-kind recovery actions to concrete buttons, dropping any
  // that don't apply here (retry needs a handler; chrome/arcore are Android-
  // only). Primary action renders solid; the rest as secondary.
  const actionButtons = (copy.actions ?? [])
    .map((action) => {
      if (action === "retry") return onRetry ? { label: "Try again", onClick: onRetry } : null;
      if (action === "chrome") return android ? { label: "Open in Chrome", onClick: openInChrome } : null;
      if (action === "arcore") return android ? { label: "Get Google Play Services for AR", onClick: openArCoreUpdate } : null;
      return null;
    })
    .filter((b): b is { label: string; onClick: () => void } => b !== null);

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center pointer-events-auto z-[60] px-8 text-center"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(21,11,42,0.45) 0%, rgba(21,11,42,0.78) 80%, rgba(21,11,42,0.95) 100%), url(${Bg})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        minHeight: "100dvh",
        color: "var(--brand-text)",
      }}
      role="alert"
    >
      <img
        src={Wordmark}
        alt="Murmurations"
        className="w-full max-w-[220px] mb-10 opacity-90"
        style={{ filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.35))" }}
      />

      {/* Warning glyph */}
      <svg
        className="w-12 h-12 mb-5 text-white/90"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.6}
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.008v.008H12v-.008Z"
        />
      </svg>

      <h2 className="text-white text-xl font-medium mb-3">{copy.title}</h2>
      <p className="text-white/80 text-sm leading-relaxed max-w-sm mb-8">{copy.body}</p>

      <div className="flex flex-col items-stretch gap-3 w-full max-w-[300px]">
        {actionButtons.map((btn, i) => (
          <button
            key={btn.label}
            type="button"
            onClick={btn.onClick}
            className={
              i === 0
                ? "px-6 py-3 rounded-full bg-white text-[var(--brand-bg-deep,#150b2a)] text-sm font-semibold transition-colors"
                : "px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm font-medium backdrop-blur transition-colors"
            }
          >
            {btn.label}
          </button>
        ))}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 rounded-full text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            &larr; Back to site
          </button>
        )}
      </div>

      {/* Debug footer — always visible so a screenshot identifies the failure. */}
      <div className="absolute bottom-4 left-0 right-0 px-6">
        <p className="text-white/40 text-[11px] font-mono break-words">
          {copy.code}
          {error.detail ? ` · ${error.detail}` : ""}
        </p>
      </div>
    </div>
  );
}
